import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ProductShowcase = () => {
  const [selectedProduct, setSelectedProduct] = useState('RUT956');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);
  const componentRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleScroll = () => {
      if (componentRef.current) {
        const rect = componentRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
        setIsVisible(isVisible);
      }
    };

    checkMobile();
    handleScroll();

    window.addEventListener('resize', checkMobile);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const products = {
    RUT956: {
      name: 'RUT956',
      images: ['/product.png', '/rut956-2.png', '/rut956-3.png', '/rut956-4.png'],
      description: 'Advanced 4G/LTE router with enhanced security features and industrial-grade connectivity.',
      features: ['4G/LTE (Cat 4)', '3G, 2G', 'IP55', 'PoE-In/Out'],
    },
    RUT241: {
      name: 'RUT241',
      images: ['/rut241-1.png', '/rut241-2.png', '/rut241-3.png', '/rut241-4.png'],
      description: 'Professional cellular router for industrial applications.',
      features: ['4G LTE Cat 4', 'Wi-Fi 802.11b/g/n', 'Dual Ethernet ports', 'Digital I/O'],
    },
    RUTm50: {
      name: 'RUTm50',
      images: ['/rutm50-5.png', '/rutm50-4.png', '/rutm50-2.png', '/rutm50-6.png'],
      description: 'High-performance industrial cellular router.',
      features: ['4G LTE', 'Wi-Fi', 'GPS', 'USB'],
    },
    RUT241_eSIM: {
      name: 'RUT241 eSIM',
      images: ['/rut241-sim-1.png', '/rut241-sim-2.png', '/rut241-sim-4.png', '/rut241-sim-5.png'],
      description: 'High-performance industrial cellular router.',
      features: ['4G LTE', 'Wi-Fi', 'GPS', 'USB'],
    }
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      width: '100%',
      margin: '0 auto',
      padding: '24px',
      boxSizing: 'border-box',
      opacity: isVisible ? 1 : 0,
      transform: `translateY(${isVisible ? '0' : '20px'})`,
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
    },
    headerContainer: {
      textAlign: 'center',
      marginBottom: '16px',
      padding: '10px 0'
    },
    mainHeading: {
      fontSize: 'clamp(20px, 4vw, 24px)',
      fontWeight: '400',
      color: '#1e293b',
      margin: '0 0 -15px 0',
      fontFamily: 'Oswald, sans-serif'
    },
    subHeading: {
      fontSize: 'clamp(32px, 6vw, 60px)',
      fontWeight: '700',
      color: '#400684',
      marginBottom:'-30px',
      margin: '0',
      fontFamily: 'Oswald, sans-serif'
    },
    tabContainer: {
      position: 'relative',
      marginBottom: '32px',
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      maxWidth: '600px',
      margin: '0 auto 32px auto'
    },
    scrollContainer: {
      display: 'flex',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      gap: '8px',
      padding: '16px',
      paddingLeft: isMobile ? '48px' : '16px',
      paddingRight: isMobile ? '48px' : '16px',
      width: '100%',
      margin: '0 auto',
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      justifyContent: 'center',
      '&::-webkit-scrollbar': {
        display: 'none'
      }
    },
    navButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '50%',
      width: '32px',
      height: '32px',
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 10,
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    leftButton: {
      left: 0
    },
    rightButton: {
      right: 0
    },
    tab: (isSelected) => ({
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      background: isSelected ? '#400684' : '#f1f5f9',
      color: isSelected ? 'white' : '#1e293b',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.3s ease',
      fontWeight: isSelected ? '600' : '400',
      scrollSnapAlign: 'center',
      fontSize: '14px'
    }),
    productCard: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '24px',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
      animation: 'fadeIn 0.3s ease-in-out',
      width: '100%',
      boxSizing: 'border-box'
    },
    imageGallery: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '12px',
      width: '100%'
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      height: '0',
      paddingBottom: '75%'
    },
    image: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease'
    },
    imageNav: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '32px',
      height: '32px',
      background: 'rgba(255, 255, 255, 0.8)',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    imageNavLeft: {
      left: '10px'
    },
    imageNavRight: {
      right: '10px'
    },
    imageDots: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '8px',
      zIndex: 2
    },
    imageDot: (isActive) => ({
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: isActive ? '#400684' : 'rgba(255, 255, 255, 0.5)',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }),
    productInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    title: {
      fontSize: 'clamp(20px, 4vw, 24px)',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom:'-5px',
      margin: '0'
    },
    description: {
      color: '#64748b',
      lineHeight: '1.6',
      margin: '0',
      fontSize: '14px'
    },
    featuresList: {
      listStyle: 'none',
      padding: '0',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      color: 'black',
      gap: '12px',
      fontSize: '14px'
    },
    featureDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#400684'
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#400684',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
       fontFamily: 'Oswald, sans-serif',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      width: 'fit-content',
      fontSize: '14px',
      alignSelf: 'flex-start'
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -160 : 160;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
const handleCardClick = (productId) => {
  // You can modify this URL structure according to your routing setup
  window.location.href = `/product/${productId}`;
};

  const navigateImage = (direction) => {
    const currentProduct = products[selectedProduct];
    const maxIndex = currentProduct.images.length - 1;
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentImageIndex === 0 ? maxIndex : currentImageIndex - 1;
    } else {
      newIndex = currentImageIndex === maxIndex ? 0 : currentImageIndex + 1;
    }

    setCurrentImageIndex(newIndex);
  };

  return (
    <div ref={componentRef} style={styles.container}>
      <div style={styles.headerContainer}>
        <h1 style={styles.mainHeading}>Case Studies</h1>
        <h2 style={styles.subHeading}>Detailing of our products</h2>
      </div>

      <div style={styles.tabContainer}>
        <button 
          onClick={() => scroll('left')} 
          style={{...styles.navButton, ...styles.leftButton}}
        >
          <ChevronLeft size={16} />
        </button>
        
        <div ref={scrollContainerRef} style={styles.scrollContainer}>
          {Object.keys(products).map((productId) => (
            <button
              key={productId}
              style={styles.tab(productId === selectedProduct)}
              onClick={() => {
                setSelectedProduct(productId);
                setCurrentImageIndex(0);
              }}
            >
              {products[productId].name}
            </button>
          ))}
        </div>

        <button 
          onClick={() => scroll('right')} 
          style={{...styles.navButton, ...styles.rightButton}}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div style={styles.productCard}>
        <div style={styles.imageGallery}>
          <div style={styles.imageContainer}>
            <img
              src={products[selectedProduct].images[currentImageIndex]}
              alt={`${products[selectedProduct].name}`}
              style={styles.image}
            />
            
            <button
              onClick={() => navigateImage('prev')}
              style={{...styles.imageNav, ...styles.imageNavLeft}}
            >
              <ChevronLeft size={16} />
            </button>
            
            <button
              onClick={() => navigateImage('next')}
              style={{...styles.imageNav, ...styles.imageNavRight}}
            >
              <ChevronRight size={16} />
            </button>

            <div style={styles.imageDots}>
              {products[selectedProduct].images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  style={styles.imageDot(index === currentImageIndex)}
                />
              ))}
            </div>
          </div>
        </div>

        <div style={styles.productInfo}>
          <h2 style={styles.title}>{products[selectedProduct].name}</h2>
          <p style={styles.description}>{products[selectedProduct].description}</p>
          
          <div>
            <h3 style={{...styles.title, fontSize: '18px'}}>Key Features:</h3>
            <ul style={styles.featuresList}>
              {products[selectedProduct].features.map((feature, index) => (
                <li key={index} style={styles.featureItem}>
                  <span style={styles.featureDot}></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <button 
      style={styles.button}
      onClick={(e) => {
        e.stopPropagation(); // Prevent card click when clicking button
        handleCardClick(selectedProduct);
      }}
    >
            Explore More
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;